import isNil from 'lodash/isNil';
import { type ComponentProps, type ReactNode, useCallback, useEffect, useState } from 'react';

import { Button, ButtonVariant } from '@ui/components/base/button';
import { Div } from '@ui/components/base/div';
import { Popover as PopoverRoot, PopoverContent, PopoverTitle } from '@ui/components/base/popover';
import { XIcon } from '@ui/components/icons';
import { cn } from '@ui/lib/utils';

const DRILL_SIDE_OFFSET = 8;

interface DrillAnchor {
  getBoundingClientRect: () => DOMRect;
  contextElement?: Element;
}

const pointDrillAnchor = (element: Element, x: number, y: number): DrillAnchor => {
  const initialRect = element.getBoundingClientRect();
  const offsetX = x - initialRect.left;
  const offsetY = y - initialRect.top;
  return {
    contextElement: element,
    getBoundingClientRect: () => {
      const rect = element.getBoundingClientRect();
      return new DOMRect(rect.left + offsetX, rect.top + offsetY, 0, 0);
    },
  };
};

interface DrillPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchor: DrillAnchor | null;
  title: string;
  subtitle?: string;
  color?: string;
  closeLabel: string;
  toolbar?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
  initialFocus?: ComponentProps<typeof PopoverContent>['initialFocus'];
}

const DrillPopover = ({
  open,
  onOpenChange,
  anchor,
  title,
  subtitle,
  color,
  closeLabel,
  toolbar,
  footer,
  children,
  className,
  initialFocus,
}: DrillPopoverProps) => {
  const [listMinHeight, setListMinHeight] = useState<number | undefined>(undefined);
  const measureList = useCallback(
    (list: HTMLDivElement | null) => {
      if (open && !isNil(list)) {
        setListMinHeight((current) => current ?? parseFloat(getComputedStyle(list).height));
      }
    },
    [open]
  );

  useEffect(() => {
    if (!open) {
      setListMinHeight(undefined);
    }
  }, [open]);

  return (
    <PopoverRoot open={open} onOpenChange={onOpenChange}>
      <PopoverContent
        anchor={anchor}
        align="start"
        sideOffset={DRILL_SIDE_OFFSET}
        initialFocus={initialFocus}
        className={cn('max-h-115 w-90 gap-0 overflow-hidden p-0', className)}
      >
        <Div row className="flex-none items-center gap-2.5 border-b border-border py-3 pr-3 pl-3.5">
          {!isNil(color) && <span className="size-2.5 shrink-0 rounded-xs" style={{ background: color }} />}
          <Div column className="min-w-0 flex-1">
            <PopoverTitle className="truncate text-sm font-extrabold text-fg-strong">{title}</PopoverTitle>
            {!isNil(subtitle) && <span className="text-xs-plus font-semibold text-fg-muted">{subtitle}</span>}
          </Div>
          <Button
            variant={ButtonVariant.GHOST}
            size="icon-sm"
            aria-label={closeLabel}
            onClick={() => onOpenChange(false)}
          >
            <XIcon />
          </Button>
        </Div>
        {!isNil(toolbar) && <Div className="flex-none border-b border-border px-3 py-2.5">{toolbar}</Div>}
        <Div
          ref={measureList}
          column
          className="min-h-0 flex-1 overflow-y-auto p-1.5"
          style={{ minHeight: listMinHeight }}
        >
          {children}
        </Div>
        {!isNil(footer) && <Div className="flex-none border-t border-border p-2">{footer}</Div>}
      </PopoverContent>
    </PopoverRoot>
  );
};

export { DrillPopover, pointDrillAnchor };
export type { DrillAnchor, DrillPopoverProps };
